import { useSetAtom } from 'jotai';
import { setSelectedActivityAtom } from '../atoms';
import { formatDate } from '@/utils/formatDate';
import thumbImage from '@/assets/thumb.png';
import iconImage from '@/assets/icon.png';
import petImage from '@/assets/pet.png';
import unknownImage from '@/assets/uknown.png';
import avatarImage from '@/assets/Avatar.png';
import type { Activity } from '@/features/activity/types';
import { Card } from '@/components/ui/card';

interface ActivityCardProps {
  activity: Activity;
}

  // Activity Icon Component
  const ActivityIcon = ({ title }: { title: string }) => {
    const lowerTitle = title.toLowerCase();
    let icon = iconImage;
    let borderColor = 'border-green-500';
    
    if (lowerTitle.includes('convict')) {
      icon = iconImage;
      borderColor = 'border-red-500';
    } else if (lowerTitle.includes('pet')) {
      icon = petImage;
      borderColor = 'border-orange-500';
    } else if (lowerTitle.includes('unknown')) {
      icon = unknownImage;
      borderColor = 'border-gray-500';
    }
    
    return (
      <div className={`w-6 h-6 rounded-full border ${borderColor} overflow-hidden`}>
        <img 
          src={icon} 
          alt="Activity icon"
          className="w-full h-full object-cover"
        />
      </div>
    );
  };

  // Activity Thumbnail Component
  const ActivityThumbnail = ({ activity }: { activity: Activity }) => {
    return (
      <div className="w-[122px] h-[68px] overflow-hidden flex-shrink-0">
        {activity.imageUrl && activity.imageUrl.trim() !== '' ? (
          <img 
            src={activity.imageUrl} 
            alt={activity.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = thumbImage;
            }}
          />
        ) : activity.s3VideoUrl ? (
          <img 
            src={thumbImage} 
            alt={activity.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <img 
            src={thumbImage} 
            alt={activity.title}
            className="w-full h-full object-cover"
          />
        )}
      </div>
    );
  };
  
export function ActivityCard({ activity }: ActivityCardProps) {
  const setSelectedActivity = useSetAtom(setSelectedActivityAtom);
  
  const handleClick = () => {
    setSelectedActivity(activity);
  };

  // Helper function to format time
  const formatTime = (start: number, end: number) => {
    const duration = end - start;
    if (duration < 60) {
      return `${duration} min`;
    } else if (duration < 1440) {
      return `${Math.floor(duration / 60)} hr`;
    } else {
      return `${Math.floor(duration / 1440)} days`;
    }
  };

  return (
    <Card
      onClick={handleClick}
      className="w-full h-[87px] bg-white/25 cursor-pointer hover:bg-white/50 hover:border hover:border-white"
    >
      <div className="flex h-full p-2 gap-3">
        {/* Thumbnail - Left side */}
        <ActivityThumbnail activity={activity} />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col justify-between min-w-0">
          {/* Top Row - Icon, Title, Date */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {/* Activity Type Icon */}
              <div className="flex-shrink-0">
                <ActivityIcon title={activity.title} />
              </div>

              {/* Activity Title */}
              <div className="text-sm font-medium text-gray-900 truncate">
                {activity.title}
              </div>
            </div>

            {/* Date ID */}
            <div className="text-xs text-gray-400 text-right italic flex-shrink-0 ml-2">
              {activity.date ? formatDate(activity.date) : '25-130-50102'}
            </div>
          </div>

          {/* Bottom Row - Description, User Info, Time */}
          <div className="flex items-center">
            {/* Entry Point Description */}
            <div className="text-sm text-gray-600 min-w-0 flex-1">
              <span>Entered through: </span>
              <span className="font-medium">{activity.roleWithinProperty.trim() !== '' ? activity.roleWithinProperty : 'Unknown'}</span>
            </div>

            {/* User Info - Centered */}
            <div className="flex items-center gap-2 flex-shrink-0 mx-8">
              {/* User Avatar */}
              <div className="w-4 h-4 rounded-full border border-white/12 overflow-hidden">
                <img 
                  src={avatarImage} 
                  alt="User avatar"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* User Name */}
              <div className="text-xs text-gray-600">
                {activity.name.trim() !== '' ? activity.name : 'No name'}
              </div>
            </div>

            {/* Time Badge */}
            <div className="text-xs text-gray-500 text-right whitespace-nowrap flex-shrink-0 flex-1 flex justify-end">
              {activity.markers?.[0] ? formatTime(activity.markers[0].start, activity.markers[0].end) : '3 min'}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
